import type { GroupNode, PipelineNode, Scheduler, StickyNote } from '@/mocks/pipeline';
import { NODE_W } from './flowUtils';
import { PROMPT_GAP, PROMPT_H, PROMPT_W, PROMPT_X, PROMPT_Y, skillStartX } from './promptNode';
import type { KV } from './studioPersist';

export const NODE_VISUAL_H = 150;
export const SCHEDULER_W = 240;
export const SCHEDULER_H = 148;
const X_STEP = NODE_W + PROMPT_GAP;
const COLLIDE_GAP = 24;

export type Placed = { x: number; y: number };
export type CanvasLayout = {
  nodes: { id: string; x: number; y: number }[];
  groups?: { id: string; x: number; y: number }[];
  stickies?: { id: string; x: number; y: number }[];
  schedulers?: { id: string; x: number; y: number }[];
};

export type Rect = { x: number; y: number; w: number; h: number };

export function layoutStorageKey(outcomeId: string | null | undefined, projectId: string | null | undefined): string {
  const oid = (outcomeId || '').trim();
  if (oid) return `sh_canvas_layout:${oid}`;
  const pid = (projectId || '').trim();
  if (pid) return `sh_canvas_layout_proj:${pid}`;
  return 'sh_canvas_layout';
}

export function promptRect(): Rect {
  return { x: PROMPT_X, y: PROMPT_Y, w: PROMPT_W, h: PROMPT_H };
}

export function skillRect(n: Placed): Rect {
  return { x: n.x, y: n.y, w: NODE_W, h: NODE_VISUAL_H };
}

export function schedulerRect(n: Placed): Rect {
  return { x: n.x, y: n.y, w: SCHEDULER_W, h: SCHEDULER_H };
}

export function coord(value: number | undefined | null): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function isUnplaced(n: Placed): boolean {
  return coord(n.x) === 0 && coord(n.y) === 0;
}

export function rectsOverlap(a: Rect, b: Rect, gap = COLLIDE_GAP): boolean {
  return a.x < b.x + b.w + gap && a.x + a.w + gap > b.x && a.y < b.y + b.h + gap && a.y + a.h + gap > b.y;
}

function overlapsAny(rect: Rect, others: Rect[]): boolean {
  return others.some((o) => rectsOverlap(rect, o));
}

function nextOpenX(y: number, occupied: Rect[], w = NODE_W, h = NODE_VISUAL_H): number {
  let x = skillStartX();
  const prompt = promptRect();
  for (let i = 0; i < 40; i++) {
    const rect = { x, y, w, h };
    if (!rectsOverlap(rect, prompt) && !overlapsAny(rect, occupied)) return x;
    x += X_STEP;
  }
  return x;
}

export function applySavedPositions<T extends { id: string; x: number; y: number }>(
  items: T[],
  saved: { id: string; x: number; y: number }[] | null | undefined,
): T[] {
  if (!saved?.length) return items;
  const byId = new Map(saved.map((s) => [s.id, s]));
  return items.map((item) => {
    const hit = byId.get(item.id);
    if (!hit) return item;
    return { ...item, x: coord(hit.x), y: coord(hit.y) };
  });
}

export function layoutCanvasNodes<T extends { id: string; x: number; y: number }>(nodes: T[]): T[] {
  const prompt = promptRect();
  const allUnplaced = nodes.length > 0 && nodes.every((n) => isUnplaced(n));
  const occupied: Rect[] = [prompt];
  return nodes.map((n, i) => {
    if (allUnplaced) {
      const placed = { ...n, x: skillStartX() + i * X_STEP, y: PROMPT_Y };
      occupied.push(skillRect(placed));
      return placed;
    }
    let x = coord(n.x);
    let y = coord(n.y);
    if (isUnplaced(n) || rectsOverlap({ x, y, w: NODE_W, h: NODE_VISUAL_H }, prompt)) {
      y = y === 0 ? PROMPT_Y : y;
      x = nextOpenX(y, occupied);
    } else if (occupied.slice(1).some((o) => rectsOverlap({ x, y, w: NODE_W, h: NODE_VISUAL_H }, o))) {
      x = nextOpenX(y, occupied);
    }
    const placed = { ...n, x, y };
    occupied.push(skillRect(placed));
    return placed;
  });
}

export function layoutCanvasSchedulers<T extends { id: string; x: number; y: number }>(schedulers: T[]): T[] {
  const prompt = promptRect();
  const occupied: Rect[] = [prompt];
  return schedulers.map((s) => {
    let x = coord(s.x);
    let y = coord(s.y);
    if (isUnplaced(s) || rectsOverlap({ x, y, w: SCHEDULER_W, h: SCHEDULER_H }, prompt)) {
      y = y === 0 ? PROMPT_Y : y;
      x = nextOpenX(y, occupied, SCHEDULER_W, SCHEDULER_H);
    } else if (occupied.slice(1).some((o) => rectsOverlap({ x, y, w: SCHEDULER_W, h: SCHEDULER_H }, o))) {
      x = nextOpenX(y, occupied, SCHEDULER_W, SCHEDULER_H);
    }
    const placed = { ...s, x, y };
    occupied.push(schedulerRect(placed));
    return placed;
  });
}

export function parseCanvasLayout(raw: string | null): CanvasLayout | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CanvasLayout;
    if (!Array.isArray(parsed?.nodes)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readCanvasLayout(key: string, storage: KV): CanvasLayout | null {
  return parseCanvasLayout(storage.getItem(key));
}

export function writeCanvasLayout(
  key: string,
  nodes: PipelineNode[],
  groups: GroupNode[],
  stickies: StickyNote[],
  storage: KV,
  schedulers: Scheduler[] = [],
): void {
  const payload: CanvasLayout = {
    nodes: nodes.map((n) => ({ id: n.id, x: Math.round(coord(n.x)), y: Math.round(coord(n.y)) })),
    groups: groups.map((g) => ({ id: g.id, x: Math.round(coord(g.x)), y: Math.round(coord(g.y)) })),
    stickies: stickies.map((s) => ({ id: s.id, x: Math.round(coord(s.x)), y: Math.round(coord(s.y)) })),
    schedulers: schedulers.map((s) => ({ id: s.id, x: Math.round(coord(s.x)), y: Math.round(coord(s.y)) })),
  };
  storage.setItem(key, JSON.stringify(payload));
}
